/**
 * Working-state codec of the stored game state.
 *
 * The backend stores `PlayerProfile.gameState` as `{ format: 'fwp-state-v1', player, map_info }`, written by
 * FWP.serialize_state through as3.toJsonState: ProtectedInt -> {"$pi": n}, sparse arrays ->
 * {"$sparse": length, "e": [[key, value], ...]}, NaN/Infinity -> {"$n": "NaN"|"Infinity"|"-Infinity"},
 * undefined -> {"$u": 1}. FWP.apply_state reads it back with as3.fromJsonState, rebuilding ProtectedInt instances.
 *
 * Rules work on plain data instead (`decodeState`): ProtectedInt values become plain int32 numbers, so ported code
 * reads `player.gold` where the game reads `player.gold.value`. The decoder remembers which paths were ProtectedInt,
 * and `encodeState` wraps exactly those paths again - plus the ProtectedInt fields of a freshly created player
 * (PROTECTED_INT_FIELDS), so a state is always stored the way apply_state expects it. Storing into a ProtectedInt
 * truncates to int32 in the game (setter and constructor use as3.int), and the encoder does the same.
 *
 * For a state the game produced, `encodeState(decodeState(s))` is byte-identical to `s` (JSON text).
 */
import type { JsonValue } from '../../shared/json';
import { int } from './as3';
import { playerLevel, type GameDefs } from './defs';

/** Format tag of the stored state (same value as GAME_STATE_FORMAT in src/game/services.ts). */
export const GAME_STATE_FORMAT = 'fwp-state-v1';

/**
 * The player fields FWP.create_new_params creates as `new ProtectedInt(...)` (FWP.as:17801-17990; the legacy
 * importer read_storage_45 creates the same ten). They are ProtectedInt in every state the game builds.
 */
export const PROTECTED_INT_FIELDS = ['attack', 'protect', 'health', 'worms', 'skill_pts', 'perk_pts', 'gold', 'diam', 'exp', 'level'] as const;
export type ProtectedIntField = (typeof PROTECTED_INT_FIELDS)[number];

/** `int(x)`: the int32 truncation of ProtectedInt / AS3 int slots (NaN -> 0, toward zero, 32-bit wrap). */
export const int32 = int;

/** One weapon copy of `player.weapons[id]`. */
export interface WeaponCopy {
  /** Upgrade rank of the copy. */
  range: number;
  stolen: boolean;
  crafted: boolean;
  [field: string]: any;
}

/**
 * The plain player object of a working state (FWP.player with ProtectedInt values as numbers). The listed fields are
 * those FWP.create_new_params creates; states of older profiles may lack a few, and the game adds more over time
 * (the index signature keeps any field the stored document has).
 */
export interface PlayerState {
  // --- ProtectedInt in the game (int32 numbers here)
  level: number;
  exp: number;
  gold: number;
  diam: number;
  attack: number;
  protect: number;
  health: number;
  worms: number;
  skill_pts: number;
  perk_pts: number;
  // --- plain fields
  perks: Record<string, number>;
  stim_behave: Record<string, number>;
  stim_to_fight: Record<string, number>;
  /** weapons[id] = [count, copy 1, ..., copy count]. */
  weapons: Record<string, any[]>;
  settings: { skill: number; help: number; sound: number; exhaust: number; no_animation: number; [field: string]: any };
  achiev: Record<string, number>;
  last_achiev: Record<string, number>;
  progress: Record<string, number>;
  quick_weapons: Record<string, any>;
  flags: Record<string, number>;
  rip: Record<string, number>;
  impl: Record<string, number>;
  objs: Record<string, number>;
  /** recipes[0] = CRAFT_DEF[0] (count), recipes[i] = 0/1 known. */
  recipes: number[];
  spec: number;
  tired: number;
  last_tired: number;
  last_enter: number;
  tween: number;
  career_max: number;
  career_attempt: number;
  missions_max: number;
  missions_attempt: number;
  survival_max: number;
  survival_attempt: number;
  boss_max: number;
  boss_attempt: number;
  perk_slots: number;
  cur_flag: number;
  cur_rip: number;
  cur_impl: number;
  view_tutorial: number;
  today_steals: number;
  last_steal: number;
  rating: number;
  today_date: number;
  today_wins: number;
  yest_wins: number;
  last_papa: number;
  papa_asked: number;
  last_reset: number;
  next_assault_payment: number;
  cur_task: number;
  last_presented_task: number;
  today_craft_sells?: number;
  [field: string]: any;
}

/** A property path from the document root, e.g. ['player', 'gold'] or ['player', 'weapons', 'uzi', 1, 'range']. */
export type StatePath = readonly (string | number)[];

/** The stored document (JSON). */
export interface StoredGameState {
  format: typeof GAME_STATE_FORMAT;
  player: JsonValue;
  map_info: JsonValue;
  [field: string]: JsonValue;
}

/**
 * Decoded state that rule ops work on. It is owned by the op that receives it: ops mutate it in place (like the game
 * mutates FWP.player) and return it.
 */
export interface WorkingState {
  player: PlayerState;
  /** FWP.map_info: the explored world map cells (sparse arrays). */
  map_info: any[];
  /** Paths that were ProtectedInt ({"$pi"}) in the decoded document; encodeState wraps them again. */
  protectedPaths: StatePath[];
  /** Any other top-level fields of the document, kept as they were (JSON). */
  extra: Record<string, JsonValue>;
}

// ------------------------------------------------------------------------------------------------ decoding

function isMarker(v: Record<string, unknown>, key: string): boolean {
  return key in v && Object.keys(v).length === 1;
}

/**
 * An own "__proto__" member of a stored document (JSON.parse creates one) is never decoded: assigned into the working
 * state it would replace the prototype of the object, so reads of absent fields (gold, level...) would find its values.
 */
const PROTO_KEY = '__proto__';

/** as3.fromJsonState that yields numbers for {"$pi"} and records their paths. */
function decodeValue(v: unknown, path: (string | number)[], found: StatePath[]): any {
  if (v === null || typeof v !== 'object') return v;
  if (Array.isArray(v)) return v.map((x, i) => decodeValue(x, [...path, i], found));
  const o = v as Record<string, any>;
  if (isMarker(o, '$u')) return undefined;
  if (isMarker(o, '$n')) return Number(o.$n);
  if (isMarker(o, '$pi')) {
    found.push(path);
    // new ProtectedInt(v) stores int(v).
    return int(Number(o.$pi));
  }
  if ('$sparse' in o && 'e' in o) {
    const arr: any[] = new Array(Number(o.$sparse));
    for (const [k, x] of o.e as [string | number, unknown][]) if (k !== PROTO_KEY) (arr as any)[k] = decodeValue(x, [...path, k], found);
    return arr;
  }
  const out: Record<string, any> = {};
  for (const k of Object.keys(o)) if (k !== PROTO_KEY) out[k] = decodeValue(o[k], [...path, k], found);
  return out;
}

/** Thrown for a document that is not a stored game state. */
export class StateFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StateFormatError';
  }
}

/** Decodes a stored game state into a fresh working state (the input is not modified). */
export function decodeState(doc: unknown): WorkingState {
  if (!doc || typeof doc !== 'object' || Array.isArray(doc)) throw new StateFormatError('game state is not an object');
  const d = doc as Record<string, JsonValue>;
  if (d.format !== GAME_STATE_FORMAT) throw new StateFormatError(`unsupported game state format ${JSON.stringify(d.format)}`);
  if (!d.player || typeof d.player !== 'object' || Array.isArray(d.player)) throw new StateFormatError('game state has no player object');
  const found: StatePath[] = [];
  const player = decodeValue(d.player, ['player'], found) as PlayerState;
  const map_info = decodeValue(d.map_info, ['map_info'], found);
  const extra: Record<string, JsonValue> = {};
  for (const k of Object.keys(d)) if (k !== 'format' && k !== 'player' && k !== 'map_info' && k !== PROTO_KEY) extra[k] = d[k];
  return { player, map_info, protectedPaths: found, extra };
}

// ------------------------------------------------------------------------------------------------ encoding

/** Trie of protected paths (keys as property-name strings). A node with `pi` wraps the value at that path. */
interface PathTrie {
  pi: boolean;
  next: Map<string, PathTrie>;
}

function newTrie(): PathTrie {
  return { pi: false, next: new Map() };
}

function addPath(t: PathTrie, path: StatePath): void {
  let n = t;
  for (const k of path) {
    const key = String(k);
    let c = n.next.get(key);
    if (!c) n.next.set(key, (c = newTrie()));
    n = c;
  }
  n.pi = true;
}

/** as3.toJsonState where "is a ProtectedInt" means "the path is protected and the value is a number". */
function encodeValue(v: any, t: PathTrie | undefined): JsonValue {
  if (t?.pi && typeof v === 'number') return { $pi: int(v) };
  if (v === undefined) return { $u: 1 };
  if (v === null || typeof v === 'string' || typeof v === 'boolean') return v;
  if (typeof v === 'number') return Number.isFinite(v) ? v : { $n: String(v) };
  if (Array.isArray(v)) {
    const keys = Object.keys(v);
    const dense = keys.length === v.length && keys.every((k, i) => k === String(i));
    if (dense) return v.map((x, i) => encodeValue(x, t?.next.get(String(i))));
    return {
      $sparse: v.length,
      e: keys.map((k) => [/^\d+$/.test(k) ? Number(k) : k, encodeValue((v as any)[k], t?.next.get(k))]),
    };
  }
  if (typeof v === 'object') {
    const out: Record<string, JsonValue> = {};
    for (const k of Object.keys(v)) out[k] = encodeValue(v[k], t?.next.get(k));
    return out;
  }
  return null; // functions, symbols, bigints: as3.toJsonState writes null
}

/**
 * Encodes a working state into the stored format: the paths the document had as ProtectedInt plus the player's
 * PROTECTED_INT_FIELDS become {"$pi": int(value)} when they hold a number; everything else is written the way
 * as3.toJsonState writes it.
 */
export function encodeState(state: WorkingState): StoredGameState {
  const trie = newTrie();
  for (const p of state.protectedPaths) addPath(trie, p);
  for (const f of PROTECTED_INT_FIELDS) addPath(trie, ['player', f]);
  return {
    format: GAME_STATE_FORMAT,
    player: encodeValue(state.player, trie.next.get('player')),
    map_info: encodeValue(state.map_info, trie.next.get('map_info')),
    ...state.extra,
  };
}

/** Deep copy of a working state (e.g. to keep the state before an op). */
export function cloneState(state: WorkingState): WorkingState {
  return decodeState(encodeState(state));
}

// ------------------------------------------------------------------------------------------------ load rules

/**
 * FWP.apply_state's load normalizations (FWP.ts apply_state; the original's read_storage ran them on every load,
 * FWP.as:16911-16918): a spec flag without 20 missions is dropped, a rating of 0 with exp > 0 becomes
 * (level - 1) * 100 - with the stored, possibly stale level, like the original - and a worn flag_russia / flag_ussr
 * (product change) is taken off. In place.
 */
export function applyStateNormalizations(p: PlayerState, defs: Pick<GameDefs, 'FLAGS_DEF'>): void {
  if (p.flags && p.flags['flag_spec'] == 1 && p.missions_max < 20) p.flags = {};
  if (p.exp > 0 && p.rating == 0) p.rating = (p.level - 1) * 100;
  for (const k of ['flag_russia', 'flag_ussr']) {
    if (defs.FLAGS_DEF && p.cur_flag == defs.FLAGS_DEF[k].id) p.cur_flag = 0;
  }
}

/**
 * The load rules of a stored state (design D2), in place: applyStateNormalizations (FWP.apply_state), then the level
 * derived from exp (FWP.net_initialized `player.level.value = get_player_level(exp)`; the stored level can lag behind
 * exp). The session.enter op runs it for every loaded profile, before its time refresh (net_refresh_time /
 * modify_last_enter touch none of these fields, so the order the original had does not matter). Returns `state`.
 */
export function normalizeLoaded(state: WorkingState, defs: Pick<GameDefs, 'FLAGS_DEF' | 'LEVEL_THRESHOLD'>): WorkingState {
  const p = state.player;
  applyStateNormalizations(p, defs);
  p.level = int(playerLevel(defs, p.exp));
  return state;
}
